import React, {useCallback, useEffect, useMemo, useState} from 'react';
import classNames from 'classnames';
import Grid from '@material-ui/core/Grid';
import {useTranslation} from 'react-i18next';
import TextField from '../../components/TextField/TextField';
import {useSelector} from 'react-redux';
import {useFormikContext} from 'formik';
import DeviceProfileDetailsActionKeyButton from './DeviceProfileDetailsActionKeyButton';
import {DeviceProfileDetailsActionKeyRowData} from './DeviceProfileDetailsActionKeyRow.utils';
import DeviceProfileDetailsActionKeyRow from './DeviceProfileDetailsActionKeyRow';
import {Permission, PermissionType} from '../../store/types/Permission';
import {DeviceProfileDetailsTabFormType, DeviceProfileDetailsTabProps, useStyle} from './DeviceProfileDetailsTab.utils';
import {getRoute, Routes} from '../../routes/routes';
import SelectField from '../../components/SelectField/SelectField';
import {ReduxState} from '../../store/types';
import {DropDownDictionaryItem} from '../CallHistory/ActiveCalls/ActiveCalls.utils';
import {useLocation} from 'react-router-dom';
import {ReactComponent as OpenExternalLink} from '../../assets/OpenExternalLink.svg';
import PreventLeavingPage from '../../components/AlertDialog/PreventLeavingPage';
import {getPermission} from '../../utils/permissions/getPermission';
import {useRawPermissions} from '../../hooks/usePermissions';
import Loader from '../../components/Loader/Loader';
import DeviceProfileDetailsActions from './DeviceProfileDetailsActions';
import Switch from '../../components/Switch/Switch';
import {DEVICE_PROFILE_IS_NOT_UNIQUE_API_ERROR} from '../../store/sagas/devices/saga';
import DeviceProfileKeysPager from './DeviceProfileKeysPager';

const DeviceProfileDetailsTab: React.FC<DeviceProfileDetailsTabProps> = (
    {
        className,
        dataQa,
        uaProfileMetaInfo,
        readonly = false,
        onChangeTemplate,
        handleOnSave,
    }) => {
    const classes = useStyle();
    const {t} = useTranslation();
    const [activeButtonValue, setActiveButtonValue] = useState<string | undefined>(undefined);
    const permissions = useRawPermissions();
    const profilesPermission = getPermission(permissions, ...Permission.Inventory.DeviceProfiles.value);
    const readOnlyByPermissions = profilesPermission !== PermissionType.Visible;
    const i_customer: number = useSelector((state: ReduxState) => state.generic?.sessionData?.i_customer ?? 0);

    const [currentPageSoftKeys, setCurrentPageSoftKeys] = useState<number>(0);
    const [currentPageLineKeys, setCurrentPageLineKeys] = useState<number>(0);
    const [currentPageExpansionKeys, setCurrentPageExpansionKeys] = useState<number>(0);

    const {
        isLoadingMetadata,
        isTypeListLoading,
        isLoadingTemplates,
        isUpdatingOptionsTemplate,
        isLoadingProfileDetails,
        updateOptionsTemplateApiError,
        templates,
        ua_type_list,
        createdProfileId
    } = useSelector((state: ReduxState) => state.devices);
    
    const isReadOnly = useMemo(() => {
        return readonly || readOnlyByPermissions;
    }, [readonly, readOnlyByPermissions]);

    const isLoading = useMemo(() => {
        return isLoadingMetadata 
            || isTypeListLoading 
            || isLoadingTemplates
            || !i_customer 
            || isUpdatingOptionsTemplate
            || isLoadingProfileDetails;
    }, [isLoadingMetadata, isTypeListLoading, isLoadingTemplates, i_customer, 
            isUpdatingOptionsTemplate, isLoadingProfileDetails]);

    const scale = 0.5;

    const location = useLocation();
    const isCopy = useMemo(() => {
        const url = location.pathname ?? '';
        const pattern = Routes.DeviceProfilesCopy.replace(":id", "(\\d){1,9}");
        const isMatch = url.match(pattern);
        return !!isMatch;
    }, [location]);

    const {
        values,
        setFieldError,
        setFieldValue,
        handleChange,
        errors,
        dirty
    } = useFormikContext<DeviceProfileDetailsTabFormType>();

    const changeFunctionCallBack = useCallback((item: DeviceProfileDetailsActionKeyRowData, newActionValue: string) => {
        const indxOfSoftKey = values.softKeys.findIndex(e => e.itemIdentifierInternal === item.itemIdentifierInternal);
        const indxOfLineKey = values.lineKeys.findIndex(e => e.itemIdentifierInternal === item.itemIdentifierInternal);
        const indxOfExpansionKey = values.expansionKeys.findIndex(e => e.itemIdentifierInternal === item.itemIdentifierInternal);
        if(indxOfSoftKey !== -1) {
            setFieldValue('softKeys[' + indxOfSoftKey + '].selectedAction', newActionValue);
            const configuration = values.softKeys[indxOfSoftKey].actionsConfiguration
                ?.find(e => e.type === newActionValue)
                ?.action_attributes;
            setFieldValue('softKeys[' + indxOfSoftKey + '].selectedActionConfiguration', configuration);
        }
        else if(indxOfLineKey !== -1) {
            setFieldValue('lineKeys[' + indxOfLineKey + '].selectedAction', newActionValue);
            const configuration = values.lineKeys[indxOfLineKey].actionsConfiguration
                ?.find(e => e.type === newActionValue)
                ?.action_attributes;
            setFieldValue('lineKeys[' + indxOfLineKey + '].selectedActionConfiguration', configuration);
        }
        else if(indxOfExpansionKey !== -1) {
            setFieldValue('expansionKeys[' + indxOfExpansionKey + '].selectedAction', newActionValue);
            const configuration = values.expansionKeys[indxOfExpansionKey].actionsConfiguration
                ?.find(e => e.type === newActionValue)
                ?.action_attributes;
            setFieldValue('expansionKeys[' + indxOfExpansionKey + '].selectedActionConfiguration', configuration);
        }
    }, [values, setFieldValue]);

    const changeValueCallBack = useCallback((item: DeviceProfileDetailsActionKeyRowData, newValue: string) => {
        const indxOfSoftKey = values.softKeys.findIndex(e => e.itemIdentifierInternal === item.itemIdentifierInternal);
        const indxOfLineKey = values.lineKeys.findIndex(e => e.itemIdentifierInternal === item.itemIdentifierInternal);
        const indxOfExpansionKey = values.expansionKeys.findIndex(e => e.itemIdentifierInternal === item.itemIdentifierInternal);
        if(indxOfSoftKey !== -1) {
            setFieldValue('softKeys[' + indxOfSoftKey + '].value', newValue);
            setFieldError('softKeys[' + indxOfSoftKey + '].value', undefined)
        }
        else if(indxOfLineKey !== -1) {
            setFieldValue('lineKeys[' + indxOfLineKey + '].value', newValue);
            setFieldError('lineKeys[' + indxOfLineKey + '].value', undefined);
        }
        else if(indxOfExpansionKey !== -1) {
            setFieldValue('expansionKeys[' + indxOfExpansionKey + '].value', newValue);
            setFieldError('expansionKeys[' + indxOfExpansionKey + '].value', undefined);
        }
    }, [values, setFieldValue]);

    const changeLabelCallBack = useCallback((item: DeviceProfileDetailsActionKeyRowData, newActionValue: string) => {
        const indxOfSoftKey = values.softKeys.findIndex(e => e.itemIdentifierInternal === item.itemIdentifierInternal);
        const indxOfLineKey = values.lineKeys.findIndex(e => e.itemIdentifierInternal === item.itemIdentifierInternal);
        const indxOfExpansionKey = values.expansionKeys.findIndex(e => e.itemIdentifierInternal === item.itemIdentifierInternal);
        if(indxOfSoftKey !== -1) {
            setFieldValue('softKeys[' + indxOfSoftKey + '].label', newActionValue);
        }
        else if(indxOfLineKey !== -1) {
            setFieldValue('lineKeys[' + indxOfLineKey + '].label', newActionValue);
        }
        else if(indxOfExpansionKey !== -1) {
            setFieldValue('expansionKeys[' + indxOfExpansionKey + '].label', newActionValue);
        }
    }, [values, setFieldValue]);
    
    const changeLineCallBack = useCallback((item: DeviceProfileDetailsActionKeyRowData, newLineValue: number) => {
        const indxOfSoftKey = values.softKeys.findIndex(e => e.itemIdentifierInternal === item.itemIdentifierInternal);
        const indxOfLineKey = values.lineKeys.findIndex(e => e.itemIdentifierInternal === item.itemIdentifierInternal);
        const indxOfExpansionKey = values.expansionKeys.findIndex(e => e.itemIdentifierInternal === item.itemIdentifierInternal);
        if(indxOfSoftKey !== -1) {
            setFieldValue('softKeys[' + indxOfSoftKey + '].line', newLineValue);
        }
        else if(indxOfLineKey !== -1) {
            setFieldValue('lineKeys[' + indxOfLineKey + '].line', newLineValue);
        }
        else if(indxOfExpansionKey !== -1) {
            setFieldValue('expansionKeys[' + indxOfExpansionKey + '].line', newLineValue);
        }
    }, [values, setFieldValue]);

    const setActiveButton = useCallback((toActivate: DeviceProfileDetailsActionKeyRowData | undefined, scrollToRow = false) => {
        
        const beforeUpdateActiveId = activeButtonValue;
        setActiveButtonValue(toActivate?.itemIdentifierInternal);

        let pager = 0;
        switch(toActivate?.type) {
            case 'line_key':
                pager = (uaProfileMetaInfo?.keys_layout?.line_keys?.pager ?? 0);
                break;
            case 'expansion_key':
                pager = (uaProfileMetaInfo?.keys_layout?.expansion_keys?.pager ?? 0);
                break;
            case 'soft_key':
                pager = (uaProfileMetaInfo?.keys_layout?.soft_keys?.pager ?? 0);
                break;
        } 

        const isPaging = pager != 0;
        if(isPaging) {
            const left = (toActivate?.keyId ?? 0) % pager;
            const page = ((toActivate?.keyId ?? 0) - left) / pager;
            switch(toActivate?.type) {
                case 'line_key':
                    setCurrentPageLineKeys(page);
                    break;
                case 'expansion_key':
                    setCurrentPageExpansionKeys(page);
                    break;
                case 'soft_key':
                    setCurrentPageSoftKeys(page);
                    break;
            } 
        }
        
        if(toActivate?.itemIdentifierInternal && toActivate?.itemIdentifierInternal !== beforeUpdateActiveId) {
            if(scrollToRow) {
                const divId = 'device-profile-row-' + toActivate.itemIdentifierInternal;
                const elementRow = document.getElementById(divId);
                const root = document.getElementById('rootDeviceProfileDetailsTab-parent-id')?.parentElement;
                root?.scrollTo({
                    top: (elementRow?.offsetTop ?? 0) - 256,
                    left: 0,
                    behavior: "smooth",
                });
            }
        }
    }, [values, setFieldValue, setActiveButtonValue, activeButtonValue]);

    const templatesDropdownList = useMemo(() => {
        const returnArray: DropDownDictionaryItem<string>[] = [];
        for(const itm of templates ?? []) {
            const type = ua_type_list?.find(e => e.i_ua_type === itm.i_ua_type);
            const v: DropDownDictionaryItem<string> = {
                name: (type?.name?.length
                    ? type.name + ' - '
                    : '')
                + itm.name,
                value: itm.name
            };
            returnArray.push(v);
        }
        return returnArray;
    }, [templates, ua_type_list]);

    const onClickExternalLink = useCallback(() => {
        const profileId = templates?.find(e => e.name === values.profileName)
            ?.i_ua_config_template;
        const route = getRoute(Routes.DeviceProfilesDetails, {id: profileId});
        window.open(route, '_blank')?.focus();
    }, [values, templates]);

    const onClickPhoneBook = useCallback((id: number) => {
        const route = getRoute(Routes.DevicePhoneBookDetails, {id: id});
        window.open(route, '_blank')?.focus();
    }, [values, templates]);

    useEffect(() => {
        if(updateOptionsTemplateApiError?.faultcode === DEVICE_PROFILE_IS_NOT_UNIQUE_API_ERROR) {
            const text = t('errors:deviceProfiles.deviceProfileNameIsNotUnique');
            setFieldError('profileName', text);
        }
    }, [updateOptionsTemplateApiError, t, setFieldError]);

    const displayableSoftKeys = useMemo(() => {
        const isPaging = (uaProfileMetaInfo?.keys_layout?.soft_keys?.pager ?? 0) != 0;
        const take = uaProfileMetaInfo?.keys_layout?.soft_keys?.pager ?? 0;
        const skip = currentPageSoftKeys * take;
        return isPaging
            ? values.softKeys.slice(skip, skip + take)
            : values.softKeys;
    }, [values, uaProfileMetaInfo, currentPageSoftKeys]);

    const displayableLineKeys = useMemo(() => {
        const isPaging = (uaProfileMetaInfo?.keys_layout?.line_keys?.pager ?? 0) != 0;
        const take = uaProfileMetaInfo?.keys_layout?.line_keys?.pager ?? 0;
        const skip = currentPageLineKeys * take;
        return isPaging
            ? values.lineKeys.slice(skip, skip + take)
            : values.lineKeys;
    }, [values, uaProfileMetaInfo, currentPageLineKeys]);

    const displayableExpansionKeys = useMemo(() => {
        const isPaging = (uaProfileMetaInfo?.keys_layout?.expansion_keys?.pager ?? 0) != 0;
        const take = uaProfileMetaInfo?.keys_layout?.expansion_keys?.pager ?? 0;
        const skip = currentPageExpansionKeys * take;
        return isPaging
            ? values.expansionKeys.slice(skip, skip + take)
            : values.expansionKeys;
    }, [values, uaProfileMetaInfo, currentPageExpansionKeys]);

    if (isLoading) {
        return (<div className={classes.loader}>
            <Loader dataQa={'device-profile-details-loader'}/>
        </div>);
    }

    return (<>
        <div
            className={classNames(classes.rootDeviceProfileDetailsTab, className)}
            data-qa={dataQa}
            data-testid={'device-profile-info'}
            style={{position: 'relative'}}
            id="rootDeviceProfileDetailsTab-parent-id"
        >
            <Grid
                container
                spacing={3}
                className={classNames(
                    classes.itemsContainer
                )}
            >
                <div className={classes.dual}>
                    <div className={classes.scrollInsideDual}>
                        {readonly ? (
                            <div className={classes.profileDropdownContainer}>
                                <SelectField
                                    id={'profile-name-select-id'}
                                    label={t('screens:deviceProfiles.profileName')}
                                    items={templatesDropdownList}
                                    value={templatesDropdownList?.find(e => e.value == values.profileName) ?? {}}
                                    dataQa="profile-name-dropdown"
                                    onChange={(e, value: DropDownDictionaryItem<string>) => {
                                        setFieldValue('profileName', value.value);
                                        handleChange(e);
                                        const selectedOption = templates?.find(e => e.name === value.value);
                                        if(selectedOption) {
                                            onChangeTemplate?.(selectedOption);
                                        }
                                    }}
                                    getOptionLabel={(v: DropDownDictionaryItem<string>) => {
                                        return v?.name ?? '';
                                    }}
                                    getOptionSelected={(v: DropDownDictionaryItem<string>) => {
                                        return v.value === values.profileName;
                                    }}
                                    disableClearable
                                    classes={{
                                        container: classes.profileName
                                    }}
                                    disableAutoSettingValue
                                />
                                {templatesDropdownList?.find(e => e.value == values.profileName)
                                    && profilesPermission !== PermissionType.Hidden && (
                                    <div className={classes.externalLinkeContainer}>
                                        <OpenExternalLink onClick={onClickExternalLink}/>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <TextField
                                label={t('screens:deviceProfiles.profileName')}
                                value={values?.profileName}
                                id='profileName'
                                className={classes.profileName}
                                noTooltip
                                required
                                onChange={(e) => {
                                    setFieldValue('profileName', e.target.value);
                                    setFieldError('profileName', undefined);
                                    handleChange(e);
                                }}
                                maxLength={64}
                                helperText={errors.profileName}
                                setFieldError={setFieldError}
                            />
                        )}

                        {readonly && (values?.directories?.filter(e => e.enabled)?.length ?? 0) > 0 && (
                            <div className={classes.sectionHeader}>{t('screens:devicePhoneBooks.phonebooks')}</div>
                        )}

                        {readonly && (values?.directories?.filter(e => e.enabled)?.length ?? 0) > 0 && (<div className={classes.assignedDirectoryContainer}>
                            {values?.directories?.filter(e => e.enabled)?.map((k, indx) => (<div key={'assigned-phone-book-id' + k.i_ua_config_directory}
                                className={classes.assignedDirectoryContainer}>
                                    <div className={classNames(classes.assignedDirectoryItem, indx && classes.assignedDirectoryItemWithBorder)}>
                                        <div className={classes.assignedDirectoryItemSwitch}>
                                            <Switch
                                                checked
                                                disabled
                                                dataQa={'device-phone-book-item-selecte-switcher' + k.i_ua_config_directory}
                                                dataTestId={'device-phone-book-item-selected-switcher' + k.i_ua_config_directory}
                                            />
                                        </div>
                                        <div className={classes.assignedDirectoryItemName}>
                                            {k.name}
                                        </div>
                                        <div className={classes.assignedDirectoryItemItems}>
                                            <div className={classes.assignedDirectoryItemItemsSubTop}>{k.total_records}</div>
                                            <div className={classes.assignedDirectoryItemItemsSubBottom}>{t('screens:devicePhoneBooks.entries')}</div>
                                        </div>
                                        <div className={classes.assignedDirectoryItemNavigate}>
                                            <OpenExternalLink onClick={() => {onClickPhoneBook(k.i_ua_config_directory);}}/>
                                        </div>
                                    </div>
                            </div>))}
                        </div>)}

                        {(values?.softKeys?.length ?? 0) > 0 && (
                            <div className={classes.sectionHeader}>{t('screens:deviceProfiles.programmableKeys')}</div>
                        )}

                        {(values?.softKeys?.length ?? 0) > 0 && (
                            values?.softKeys?.map((k, indx) => (<DeviceProfileDetailsActionKeyRow
                                key={'soft_key_id' + indx}
                                item={k}
                                changeFunctionCallBack={changeFunctionCallBack}
                                changeValueCallBack={changeValueCallBack}
                                changeLabelCallBack={changeLabelCallBack}
                                changeLineCallBack={changeLineCallBack}
                                setActiveButton={setActiveButton}
                                onChange={handleChange}
                                errors={errors?.softKeys?.[indx]}
                                readonly={isReadOnly}
                                activeButtonIdentifier={activeButtonValue}
                            />))
                        )}
                        
                        {(values?.lineKeys?.length ?? 0) > 0 && (
                            <div className={classes.sectionHeader}>{t('screens:deviceProfiles.lineKeys')}</div>
                        )}
                        
                        {(values?.lineKeys?.length ?? 0) > 0 && (
                            values?.lineKeys?.map((k, indx) => (<DeviceProfileDetailsActionKeyRow
                                key={'line_key_id' + indx}
                                item={k}
                                changeFunctionCallBack={changeFunctionCallBack}
                                changeValueCallBack={changeValueCallBack}
                                changeLabelCallBack={changeLabelCallBack}
                                changeLineCallBack={changeLineCallBack}
                                setActiveButton={setActiveButton}
                                onChange={handleChange}
                                errors={errors?.lineKeys?.[indx]}
                                readonly={isReadOnly}
                                activeButtonIdentifier={activeButtonValue}
                            />))
                        )}

                        {(values?.expansionKeys?.length ?? 0) > 0 && (
                            <div className={classes.sectionHeader}>{t('screens:deviceProfiles.expansionKeys')}</div>
                        )}
                        
                        {(values?.expansionKeys?.length ?? 0) > 0 && (
                            values?.expansionKeys?.map((k, indx) => (<DeviceProfileDetailsActionKeyRow
                                key={'expansion_key_id' + indx}
                                item={k}
                                changeFunctionCallBack={changeFunctionCallBack}
                                changeValueCallBack={changeValueCallBack}
                                changeLabelCallBack={changeLabelCallBack}
                                changeLineCallBack={changeLineCallBack}
                                setActiveButton={setActiveButton}
                                onChange={handleChange}
                                errors={errors?.expansionKeys?.[indx]}
                                readonly={isReadOnly}
                                activeButtonIdentifier={activeButtonValue}
                            />))
                        )}

                    </div>
                    {uaProfileMetaInfo?.keys_layout?.image && (<div className={classes.keyPadContainer} style={{
                            minWidth: 1024 * scale,
                            minHeight: 1024 * scale,
                        }}>
                            <div className={classes.containerOfImageAndButtons}>
                                <div className={classes.imageAndButtonsContainer}>
                                    <div className={classes.phoneImageContainer} style={{
                                        width: 1024 * scale,
                                        height: 1024 * scale,
                                    }}>
                                        <img src={'data:image/gif;base64,' + uaProfileMetaInfo?.keys_layout?.image} style={{
                                            width: 1024 * scale,
                                            height: 1024 * scale,
                                        }} />
                                    </div>
                                    {
                                        displayableSoftKeys?.map((e, indx) => (<DeviceProfileDetailsActionKeyButton
                                            key={'soft_key_keyboard_' + indx}
                                            scale={scale}
                                            item={e}
                                            onClick={() => setActiveButton(e, true)}
                                            activeButtonIdentifier={activeButtonValue}
                                        />))
                                    }
                                    {
                                        displayableLineKeys?.map((e, indx) => (<DeviceProfileDetailsActionKeyButton
                                            key={'line_key_keyboard_' + indx}
                                            scale={scale}
                                            item={e}
                                            onClick={() => setActiveButton(e, true)}
                                            activeButtonIdentifier={activeButtonValue}
                                        />))
                                    }
                                    {
                                        displayableExpansionKeys?.map((e, indx) => (<DeviceProfileDetailsActionKeyButton
                                            key={'expansion_key_keyboard_' + indx}
                                            scale={scale}
                                            item={e}
                                            onClick={() => setActiveButton(e, true)}
                                            activeButtonIdentifier={activeButtonValue}
                                        />))
                                    }
                                    <DeviceProfileKeysPager 
                                        keys={uaProfileMetaInfo.keys_layout.line_keys}
                                        position={'top'}
                                        currentPage={currentPageLineKeys}
                                        setCurrentPage={setCurrentPageLineKeys}
                                    />
                                    <DeviceProfileKeysPager 
                                        keys={uaProfileMetaInfo.keys_layout.soft_keys}
                                        position={'bottom'}
                                        currentPage={currentPageSoftKeys}
                                        setCurrentPage={setCurrentPageSoftKeys}
                                    />
                                    {uaProfileMetaInfo.keys_layout.expansion_keys && (
                                        <DeviceProfileKeysPager 
                                            keys={uaProfileMetaInfo.keys_layout.expansion_keys}
                                            position={'bottom'}
                                            currentPage={currentPageExpansionKeys}
                                            setCurrentPage={setCurrentPageExpansionKeys}
                                        />
                                    )}
                                </div>
                            </div>
                    </div>)}
                </div>
            </Grid>
        </div>
        {!readonly && (
            <DeviceProfileDetailsActions />
        )}
        <PreventLeavingPage
            isOpen={(dirty || isCopy) && !readonly && !createdProfileId}
            onSave={() => {
                handleOnSave?.(values, true);
            }}
        />
    </>);
};

export default DeviceProfileDetailsTab;
